import * as wjcCore from '@mescius/wijmo';
import {Component, OnInit} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {ToasterService} from 'angular2-toaster';
import {DataService} from '@cosbeni/database';
import {ActivePromotionConfig} from '../config/active-promotion.config';
import {StimulsoftFontService} from '../services/stimulsoft-font.service';
import {TranslocoService} from '@jsverse/transloco';

declare var Stimulsoft: any;

/**
 * レポート管理 (旧 神戸オークションデザイナー /report/designer、システム管理に統合)。
 *
 * 業務ラインごとにデータソース (レポート格納クラス) が異なるため、先頭のセレクトで
 * 神戸オークション (AC_Report) / オンラインオークション (DA_Report) / 買取屋 (AP_Report)
 * を切り替える。切替でそのクラスに対しテンプレート一覧・読込・保存を行う。
 */
@Component({
    standalone: false,
    selector: 'kobeac-designer',
    templateUrl: 'designer.component.html',
})
export class KobeacDesignerComponent implements OnInit {
    // 業務ライン別データソース。key = OrientDB のレポートクラス名。
    readonly lines = [
        {key: 'AC_Report', labelKey: 'kobeac.brand'},
        {key: 'DA_Report', labelKey: 'kobeac.online_auction'},
        {key: 'AP_Report', labelKey: 'kobeac.kaitoriya'},
    ];
    reportClass = 'AC_Report';

    templates: any[] = [];
    selected: string;
    private report: any;
    private viewer: any;
    private designer: any;

    constructor(private dataService: DataService, private http: HttpClient,
                private toasterService: ToasterService, private fontService: StimulsoftFontService,
                private transloco: TranslocoService) {
    }

    ngOnInit(): void {
        this.loadTemplates();
    }

    /** 業務ライン切替時: データソースを変えて一覧を読み直す。 */
    onLineChange(): void {
        this.loadTemplates();
    }

    /** 現在の業務ライン (reportClass) のテンプレート一覧を読み込み、先頭を表示する。 */
    private loadTemplates(): void {
        this.dataService.query({
            sql: 'SELECT name FROM ' + this.reportClass,
            count: 'SELECT name FROM ' + this.reportClass,
            fetchPlan: '*:0',
        }, ActivePromotionConfig.SERVER_URL).subscribe((data: any) => {
            this.templates = wjcCore.isArray(data.results) ? data.results : [];
            this.selected = this.templates.length > 0 ? this.templates[0].name : undefined;
            this.refresh();
        });
    }

    async refresh(): Promise<void> {
        await this.fontService.ensureLoaded();
        const options = new Stimulsoft.Viewer.StiViewerOptions();
        options.appearance.scrollbarsMode = false;
        options.toolbar.showDesignButton = true;
        this.viewer = new Stimulsoft.Viewer.StiViewer(options, 'StiViewer', false);

        this.viewer.onDesignReport = (e: any) => {
            this.viewer.visible = false;
            if (this.designer == null) {
                this.designer = new Stimulsoft.Designer.StiDesigner(null, 'StiDesigner', false);
                this.designer.onExit = () => {
                    this.designer.visible = false;
                    this.viewer.visible = true;
                };
                this.designer.renderHtml('designerContent');
            }
            this.designer.visible = true;
            this.designer.report = e.report;
        };
        this.viewer.renderHtml('viewerContent');

        // 選択レポートが無ければ空のビューアだけ表示 (ライン切替でテンプレートが無い場合)。
        if (!this.selected) {
            this.report = null;
            return;
        }

        this.dataService.query({
            sql: 'SELECT FROM ' + this.reportClass + " WHERE name = '" + this.selected + "'",
            count: 'SELECT FROM ' + this.reportClass + " WHERE name = '" + this.selected + "'",
            fetchPlan: '*:0',
        }, ActivePromotionConfig.SERVER_URL).subscribe((data: any) => {
            if (!wjcCore.isArray(data.results) || data.results.length == 0) return;
            const json = typeof data.results[0].content == 'string'
                ? JSON.parse(data.results[0].content) : Object.assign({}, data.results[0].content);
            delete json['@type'];
            delete json['@version'];
            this.report = new Stimulsoft.Report.StiReport();
            this.report.load(json);
            this.report.dictionary.databases.clear();
            this.http.get(ActivePromotionConfig.PREFIX + '/assets/report-data.json').subscribe((sample: any) => {
                const dataSet = new Stimulsoft.System.Data.DataSet('data');
                dataSet.readJson(sample);
                this.report.regData(dataSet.dataSetName, 'data', dataSet);
                this.viewer.report = this.report;
                if (this.designer != null) this.designer.report = this.report;
            });
        });
    }

    saveReport(): void {
        if (!this.report || !this.selected) return;
        const content = JSON.parse(this.report.saveToJsonString());
        content['@type'] = 'd';
        const json = {name: this.selected, content: content};
        const sql = 'UPDATE ' + this.reportClass + ' CONTENT ' + JSON.stringify(json) + " UPSERT WHERE name = '" + this.selected + "'";
        this.dataService.command({sql: sql}, ActivePromotionConfig.SERVER_URL).subscribe({
            next: () => {
                this.toasterService.pop('info', this.transloco.translate('common.save'), this.transloco.translate('kobeac.saved_ok'));
                this.dataService.query({
                    sql: 'SELECT name FROM ' + this.reportClass,
                    count: 'SELECT name FROM ' + this.reportClass,
                    fetchPlan: '*:0',
                }, ActivePromotionConfig.SERVER_URL).subscribe((data: any) => {
                    if (wjcCore.isArray(data.results)) this.templates = data.results;
                });
            },
            error: () => this.toasterService.pop('error', this.transloco.translate('common.save'), this.transloco.translate('kobeac.failed_period')),
        });
    }
}
